import type { ICourseCategory, ICourseCategoryFields } from "$lib/utils/contentful/types";
import { DbUtils } from "$lib/utils/db-utils";
import { CourseLightModel, CourseModel, SessionLightModel } from "@24hbs/utils";

/**
 * Get the course category utils object
 * @param _fetch The fetch function
 * @returns The course category utils object
 */
export const useCourseCategoryUtils = (_fetch: {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
  (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
}): CourseCategory => {
  return new CourseCategory(_fetch);
};

/**
 * Course category class to help manage the course categories
 * @class
 * @name CourseCategory
 */
export class CourseCategory {
  private _courseCategories: ICourseCategory[] | null = null;
  private _fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  };

  public constructor(_fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }) {
    this._fetch = _fetch;
    this._fetchCourseCategories().then((courseCategories) => {
      this._courseCategories = courseCategories;
    });
  }

  /**
   * Initialize and load course categories if not already loaded
   */
  private async _ensureCourseCategoriesLoaded(): Promise<void> {
    if (this._courseCategories === null) {
      this._courseCategories = await this._fetchCourseCategories();
    }
  }

  /**
   * Fetch the course categories from the API
   * @returns {Promise<ICourseCategory[]>} - The course categories
   */
  private async _fetchCourseCategories(): Promise<ICourseCategory[]> {    
    try {
      // Log the URL being fetched
      const url = '/api/v1/course-categories.json';
      const res = await this._fetch(url);

      // Check response status
      if (!res.ok) {
        const errorText = await res.text();
        console.warn(`[CourseCategory] Error response: ${errorText}`)
        return [];
      }

      // Parse JSON response
      return await res.json() as ICourseCategory[];

    } catch (error) {
      // Log detailed error
      console.error("[CourseCategory] An error occurred during fetch:", error);
      return [];
    }
  }

  /**
   * Get the course category by name
   * @param {string} categoryName - The name of the course category
   * @returns {Promise<ICourseCategoryFields | null>} - The course category or null if not found
   */
  public async getCourseCategoryByName(categoryName: string): Promise<ICourseCategoryFields | null> {
    if (!categoryName) {
      return null;
    }

    await this._ensureCourseCategoriesLoaded();
    return this._courseCategories?.find(
      (category) => String(category.fields.categoryName) && String(category.fields.categoryName).toLowerCase() === categoryName.toLowerCase()
    )?.fields || null;
  }

  /**
   * Get the course category by slug
   * @param {string} slug - The slug of the course category
   * @returns {Promise<ICourseCategoryFields | null>} - The course category
   */
  public async getCourseCategoryBySlug(slug: string): Promise<ICourseCategoryFields | null> {
    await this._ensureCourseCategoriesLoaded();
    return this._courseCategories?.find((category) => category.fields.slug === slug)?.fields || null;
  }

  /**
   * Get all the course categories names
   * @returns {Promise<string[]>} - The course categories names
   */
  public async getCourseCategoriesNames(): Promise<string[]> {
    await this._ensureCourseCategoriesLoaded();
    return this._courseCategories?.map((category) => category.fields.categoryName) || [];
  }

  /**
   * Get all the course categories slugs
   * @returns {Promise<string[]>} - The course categories slugs
   */
  public async getCourseCategoriesSlugs(): Promise<string[]> {
    await this._ensureCourseCategoriesLoaded();
    return this._courseCategories?.map((category) => category.fields.slug) || [];
  }

  /**
   * Get all course categories
   * @returns {Promise<ICourseCategoryFields[]>} - The course categories
   */
  public async getAllCourseCategories(): Promise<ICourseCategoryFields[]> {
    await this._ensureCourseCategoriesLoaded();
    return this._courseCategories?.map((category) => category.fields) || [];
  }
}